import useOverflowHidden from 'hooks/common/use-overflow-hidden';
import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { Gtm } from 'tracking';

import { useDom } from 'contexts/dom';
import { useAiWeightAnalyzerStore } from 'stores/ai-weight-analyzer';
import { useShallow } from 'zustand/react/shallow';

import {
  AI_WEIGHT_ANALYZER_URL,
  AI_WEIGHT_ENTRY_POINTS,
  AI_WEIGHT_POSTMESSAGE_TYPES,
} from './constants';

const originHost = new URL(AI_WEIGHT_ANALYZER_URL).host;

export const AiWeightAnalyzerOverlay: FC = () => {
  const { formatMessage } = useIntl();
  const { modalWindowMountPointRef } = useDom();
  const router = useRouter();

  const { isOpen, triggeredFrom, petType, close } = useAiWeightAnalyzerStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      triggeredFrom: state.triggeredFrom,
      petType: state.petType,
      close: state.close,
    }))
  );

  useOverflowHidden(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [close, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: MessageEvent): void => {
      if (event.origin === '' || !event.origin.endsWith(originHost)) return;

      const type: unknown = event.data?.type;
      if (typeof type !== 'string') return;

      Gtm.track.selectElement({ element: ['ai-weight-analyzer', triggeredFrom, type, petType] });

      if (type === AI_WEIGHT_POSTMESSAGE_TYPES.backToShopClick) {
        close();
        void router.push(petType ? `/pet-profile/${petType}` : '/pet-profile');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [close, isOpen, petType, router, triggeredFrom]);

  if (!isOpen || !modalWindowMountPointRef.current) return null;

  const handleIframeLoad = (): void => {
    Gtm.track.viewElement({
      element: ['ai-weight-analyzer', AI_WEIGHT_ENTRY_POINTS.overlay, petType],
    });
  };

  const overlay = (
    <div
      className="z-modal fixed inset-0"
      role="dialog"
      aria-modal="true"
      aria-label={formatMessage({ id: 'ai-weight-analyzer:overlay:title' })}
      data-test="ai-weight-analyzer-overlay"
    >
      <button
        type="button"
        aria-label={formatMessage({ id: 'ai-weight-analyzer:overlay:close' })}
        className="bg-other-black/50 absolute inset-0 w-full cursor-default"
        onClick={close}
        data-test="ai-weight-analyzer-overlay-backdrop"
      />

      <div className="bg-brand-blue-700 absolute inset-x-0 top-14 bottom-0 flex flex-col overflow-hidden rounded-t-3xl">
        <button
          type="button"
          aria-label={formatMessage({ id: 'ai-weight-analyzer:overlay:close' })}
          onClick={close}
          className="flex shrink-0 cursor-pointer justify-center py-3"
          data-test="ai-weight-analyzer-overlay-close"
        >
          <span className="bg-grey-200 h-1.5 w-[60px] rounded" aria-hidden="true" />
        </button>

        {/* Crops 90px from the top to hide the weightmanager.petsdeli.de internal header.
            Remove once the embedded app adds a flag to suppress their header. */}
        <div className="relative flex-1 overflow-hidden">
          <iframe
            src={AI_WEIGHT_ANALYZER_URL}
            title={formatMessage({ id: 'ai-weight-analyzer:overlay:title' })}
            className="absolute -top-[90px] left-0 h-[calc(100%+90px)] w-full border-0"
            onLoad={handleIframeLoad}
            allow="camera; microphone; clipboard-write"
            data-test="ai-weight-analyzer-iframe"
          />
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, modalWindowMountPointRef.current);
};

export default AiWeightAnalyzerOverlay;
